/**
 * Web Push subscription lifecycle for the page side.
 *
 * Responsibilities:
 *   - Read the VAPID public key from `import.meta.env.VITE_VAPID_PUBLIC_KEY`
 *     and convert to the Uint8Array shape `pushManager.subscribe` wants.
 *   - Request notification permission, subscribe via the registered
 *     service worker, and POST the resulting `PushSubscription` to
 *     the server's `PUT /v1/devices/web-push-subscription` endpoint.
 *   - Surface the outcome as a small status enum so the caller can
 *     decide what (if anything) to show the user. The default flow
 *     calls this from `completePairing` post-pair — there's no UI
 *     gating because notification permission is fine to prompt
 *     immediately after a successful pair (the user just demonstrated
 *     they want notifications by pairing in the first place).
 *
 * Skip conditions (each returns a distinct enum value the caller
 * can branch on without reaching for try/catch):
 *   - `unsupported`: browser doesn't expose `navigator.serviceWorker`,
 *     `Notification`, or `PushManager`. Safari < 17, Firefox private
 *     mode, every iOS browser pre-iOS 16.4.
 *   - `vapid_missing`: build-time env var was unset. The whole push
 *     pipeline silently degrades to foreground-only SSE — no
 *     notification permission prompt fires.
 *   - `permission_denied` / `permission_dismissed`: user said no /
 *     dismissed without choosing.
 *   - `error`: subscribe / network failed; logged via `logError`.
 *   - `subscribed`: success.
 */

import { registerWebPushSubscription } from "../api/client";
import { logError } from "../lib/log";

export type SubscribeOutcome =
  | "subscribed"
  | "already_subscribed"
  | "unsupported"
  | "vapid_missing"
  | "permission_denied"
  | "permission_dismissed"
  | "error";

/**
 * VAPID public keys are URL-safe base64. Convert to the Uint8Array
 * `pushManager.subscribe({ applicationServerKey })` expects. Padding
 * is added to a multiple of 4; URL-safe `-`/`_` are mapped to
 * standard `+`/`/`.
 *
 * Spec-compliant equivalent of the snippet on every Web Push tutorial.
 */
function vapidKeyToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Allocate over a fresh ArrayBuffer so the resulting Uint8Array
  // satisfies `BufferSource` (which TS 6 narrows to
  // `Uint8Array<ArrayBuffer>`, excluding SharedArrayBuffer-backed
  // views). `new Uint8Array(length)` would technically work but TS
  // widens its inferred type up the chain — explicit ArrayBuffer
  // construction keeps the narrow type stable.
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    "PushManager" in window
  );
}

/**
 * Acquire a Push subscription and register it with the server. Safe
 * to call multiple times — `pushManager.subscribe` with the same
 * `applicationServerKey` returns the existing subscription if one
 * is already present, so a second call after the first becomes a
 * no-op apart from the server PUT (which is itself idempotent).
 *
 * `token` is the device JWT issued by `/pair/status` — the same
 * value `webStorage.get("token")` returns on the page side.
 */
export async function subscribeToWebPush(token: string): Promise<SubscribeOutcome> {
  if (!isSupported()) return "unsupported";

  const vapidPublic = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (!vapidPublic) return "vapid_missing";

  // Permission prompt. `Notification.permission` is the source of
  // truth — `requestPermission` returns the same string but on a
  // fresh prompt it also flashes the browser permission UI even when
  // the user already granted on a previous pairing session. Short-
  // circuit on `granted` so a user who re-pairs after disconnecting
  // doesn't see the prompt flicker again. (`denied` is also static
  // but still goes through `requestPermission` because some browsers
  // expose a one-time "ask again" path on a re-prompt; keeping the
  // call gives them that re-engagement opportunity.)
  let permission: NotificationPermission;
  if (Notification.permission === "granted") {
    permission = "granted";
  } else {
    try {
      permission = await Notification.requestPermission();
    } catch (err) {
      logError("ui", "web_push_permission_failed", err);
      return "error";
    }
  }
  if (permission === "denied") return "permission_denied";
  if (permission !== "granted") return "permission_dismissed";

  let subscription: PushSubscription;
  let wasExisting: boolean;
  try {
    const registration = await navigator.serviceWorker.ready;
    // Prefer the existing subscription if Chrome has already issued
    // one on a prior page load — calling `subscribe()` again with
    // the same VAPID key is technically idempotent per spec but in
    // practice some browsers (notably Firefox, historically) throw
    // `InvalidStateError`. The `getSubscription()` short-circuit
    // sidesteps that. Also lets us tell the caller via the return
    // enum whether we did real subscription work or just re-POSTed
    // an existing record.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      subscription = existing;
      wasExisting = true;
    } else {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyToUint8Array(vapidPublic),
      });
      wasExisting = false;
    }
  } catch (err) {
    logError("ui", "web_push_subscribe_failed", err);
    return "error";
  }

  // Always re-POST even when reusing an existing subscription — the
  // server-side row may have been pruned (410 Gone, manual cleanup,
  // FK cascade after a re-pair). This keeps the helper safe to call
  // from `completePairing` AND a future `pushsubscriptionchange`
  // bridge without the caller having to reason about server state.
  try {
    await registerWebPushSubscription(token, subscription.toJSON());
  } catch (err) {
    logError("messages", "web_push_register_failed", err);
    return "error";
  }

  return wasExisting ? "already_subscribed" : "subscribed";
}
